import {
  BdcStockDay,
  BdcStockDayDc,
  BdcStockDayDcUpdate,
  BdcStockDayItem,
  BdcStockDayItemUpdate,
  BdcStockDaySaveRequest,
  BdcStockDayTotals,
} from "../../../types/bdc.type";

/**
 * ขาด-เกิน on the client: the GM's typed-but-unsaved figures (the draft) laid over the day the
 * server sent, recomputed with the server's own formulas so the grid previews what a save will
 * return. The server stays the truth — after a save its answer replaces the day and the draft.
 *
 *   เกิน = ยกมา + โรงงาน − 7-11 สั่ง + รับโอน − โอนออก   (negative = ขาด)
 */

/** The largest piece count one cell may hold (BDC_STOCK_QTY_MAX on the server). */
export const STOCK_QTY_MAX = 1_000_000;
export const STOCK_NOTE_MAX_LENGTH = 200;
export const STOCK_SIGNER_MAX_LENGTH = 200;
/** GM report range, inclusive. */
export const STOCK_REPORT_MAX_DAYS = 92;

/** Piece counts the GM types; blank = 0. */
export const ZERO_FIELDS = ["factory_in", "transfer_in", "transfer_out"] as const;
/** Piece counts where blank means "not typed" (null): ยกมา falls back to the system, counts stay uncounted. */
export const NULLABLE_FIELDS = ["carry_in", "leftover", "damaged", "sold"] as const;
export const NOTE_FIELDS = ["transfer_in_note", "transfer_out_note"] as const;

export type ZeroField = (typeof ZERO_FIELDS)[number];
export type NullableField = (typeof NULLABLE_FIELDS)[number];
export type QtyField = ZeroField | NullableField;
export type NoteField = (typeof NOTE_FIELDS)[number];
export type ItemField = QtyField | NoteField;
export type DcField = "store_total" | "store_ordered";

/** Raw text as typed, per field; a field absent from the draft keeps the server's value. */
export type ItemDraft = Partial<Record<ItemField, string>>;
export type DcDraft = Partial<Record<DcField, string>>;

export interface StockDayDraft {
  items: Record<string, ItemDraft>;
  dcs: Record<string, DcDraft>;
}

export const emptyDraft = (): StockDayDraft => ({ items: {}, dcs: {} });

const isZeroField = (field: ItemField): field is ZeroField =>
  (ZERO_FIELDS as readonly string[]).includes(field);
const isNoteField = (field: ItemField): field is NoteField =>
  (NOTE_FIELDS as readonly string[]).includes(field);

// ---- parsing ----

export type ParsedQty = { ok: true; value: number | null } | { ok: false };

/** "" → null · "1,234" → 1234 · anything but a whole number 0..STOCK_QTY_MAX → not ok. */
export const parseQty = (text: string): ParsedQty => {
  const cleaned = text.replace(/[,\s]/g, "");
  if (cleaned === "") return { ok: true, value: null };
  if (!/^\d+$/.test(cleaned)) return { ok: false };
  const value = Number(cleaned);
  return value <= STOCK_QTY_MAX ? { ok: true, value } : { ok: false };
};

/** A note as it would be stored: trimmed, blank → null. */
export const noteValue = (text: string | null | undefined): string | null => {
  const trimmed = (text ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

// ---- what the server holds for each editable field ----

/** The stored value the PUT compares against: ยกมา is the override (null = the system's). */
export const storedItemValue = (item: BdcStockDayItem, field: ItemField): number | string | null => {
  if (field === "carry_in") return item.is_carry_in_overridden ? item.carry_in : null;
  return item[field];
};

/**
 * Whether a store count carries a GM override — the server's flag, which holds even for an
 * override equal to the system count. An older backend sends no flag: then a count that
 * differs from the system's is read as overridden.
 */
export const isDcOverridden = (dc: BdcStockDayDc, field: DcField): boolean => {
  const flag = field === "store_total" ? dc.is_store_total_overridden : dc.is_store_ordered_overridden;
  return flag ?? dc[field] !== systemDcValue(dc, field);
};

/** The stored override the PUT compares against (null = the system's count). */
export const storedDcValue = (dc: BdcStockDayDc, field: DcField): number | null =>
  isDcOverridden(dc, field) ? dc[field] : null;

/** The count the system has for a DC (what a blank cell falls back to). */
export const systemDcValue = (dc: BdcStockDayDc, field: DcField): number =>
  field === "store_total" ? dc.store_total_system : dc.store_ordered_system;

const textOf = (value: number | string | null): string => (value === null ? "" : String(value));

/** What an input shows: the draft's text, else the stored value. */
export const itemText = (item: BdcStockDayItem, draft: ItemDraft | undefined, field: ItemField): string =>
  draft?.[field] ?? textOf(storedItemValue(item, field));

export const dcText = (dc: BdcStockDayDc, draft: DcDraft | undefined, field: DcField): string =>
  draft?.[field] ?? textOf(storedDcValue(dc, field));

/** The value a field takes: the draft's when it parses, else the stored one. */
const qtyValue = (item: BdcStockDayItem, draft: ItemDraft | undefined, field: QtyField): number | null => {
  const text = draft?.[field];
  const stored = storedItemValue(item, field) as number | null;
  if (text === undefined) return stored;
  const parsed = parseQty(text);
  if (!parsed.ok) return stored;
  if (parsed.value === null && isZeroField(field)) return 0;
  return parsed.value;
};

const noteOf = (item: BdcStockDayItem, draft: ItemDraft | undefined, field: NoteField): string | null => {
  const text = draft?.[field];
  return text === undefined ? item[field] : noteValue(text);
};

const dcValue = (dc: BdcStockDayDc, draft: DcDraft | undefined, field: DcField): number | null => {
  const text = draft?.[field];
  if (text === undefined) return storedDcValue(dc, field);
  const parsed = parseQty(text);
  return parsed.ok ? parsed.value : storedDcValue(dc, field);
};

// ---- the server's formulas ----

export const balanceOf = (row: {
  carry_in: number;
  factory_in: number;
  system_out: number;
  transfer_in: number;
  transfer_out: number;
}): number => row.carry_in + row.factory_in - row.system_out + row.transfer_in - row.transfer_out;

/** value ÷ base × 100 at 1 dp; null when base is not positive. */
export const percentOf = (value: number, base: number): number | null =>
  base <= 0 ? null : Math.round((value / base) * 1000) / 10;

/** One row with the draft laid over it. */
export const previewItem = (item: BdcStockDayItem, draft: ItemDraft | undefined): BdcStockDayItem => {
  if (!draft) return item;
  const carryOverride = qtyValue(item, draft, "carry_in");
  const carryIn = carryOverride ?? item.carry_in_system;
  const factoryIn = qtyValue(item, draft, "factory_in") ?? 0;
  const transferIn = qtyValue(item, draft, "transfer_in") ?? 0;
  const transferOut = qtyValue(item, draft, "transfer_out") ?? 0;
  const leftover = qtyValue(item, draft, "leftover");
  const damaged = qtyValue(item, draft, "damaged");
  const sold = qtyValue(item, draft, "sold");
  const balance = balanceOf({
    carry_in: carryIn,
    factory_in: factoryIn,
    system_out: item.system_out,
    transfer_in: transferIn,
    transfer_out: transferOut,
  });
  const over = Math.max(balance, 0);
  const isCounted = leftover !== null || damaged !== null || sold !== null;
  return {
    ...item,
    carry_in: carryIn,
    is_carry_in_overridden: carryOverride !== null,
    factory_in: factoryIn,
    total_in: carryIn + factoryIn,
    transfer_in: transferIn,
    transfer_in_note: noteOf(item, draft, "transfer_in_note"),
    transfer_out: transferOut,
    transfer_out_note: noteOf(item, draft, "transfer_out_note"),
    balance,
    over,
    short: Math.max(-balance, 0),
    leftover,
    damaged,
    sold,
    is_count_mismatch: isCounted && (leftover ?? 0) + (damaged ?? 0) + (sold ?? 0) !== over,
  };
};

export const previewDc = (dc: BdcStockDayDc, draft: DcDraft | undefined): BdcStockDayDc => {
  if (!draft) return dc;
  const totalOverride = dcValue(dc, draft, "store_total");
  const orderedOverride = dcValue(dc, draft, "store_ordered");
  const storeTotal = totalOverride ?? dc.store_total_system;
  const storeOrdered = orderedOverride ?? dc.store_ordered_system;
  return {
    ...dc,
    store_total: storeTotal,
    is_store_total_overridden: totalOverride !== null,
    store_ordered: storeOrdered,
    is_store_ordered_overridden: orderedOverride !== null,
    ordered_store_pct: percentOf(storeOrdered, storeTotal),
  };
};

const sumOf = <T,>(rows: T[], pick: (row: T) => number | null): number =>
  rows.reduce((sum, row) => sum + (pick(row) ?? 0), 0);

export const totalsOf = (items: BdcStockDayItem[], dcs: BdcStockDayDc[]): BdcStockDayTotals => {
  const carryIn = sumOf(items, (i) => i.carry_in);
  const factoryIn = sumOf(items, (i) => i.factory_in);
  const transferIn = sumOf(items, (i) => i.transfer_in);
  const transferOut = sumOf(items, (i) => i.transfer_out);
  const over = sumOf(items, (i) => i.over);
  return {
    carry_in: carryIn,
    factory_in: factoryIn,
    total_in: carryIn + factoryIn,
    system_out: sumOf(items, (i) => i.system_out),
    transfer_in: transferIn,
    transfer_out: transferOut,
    over,
    short: sumOf(items, (i) => i.short),
    leftover: sumOf(items, (i) => i.leftover),
    damaged: sumOf(items, (i) => i.damaged),
    sold: sumOf(items, (i) => i.sold),
    store_total: sumOf(dcs, (d) => d.store_total),
    store_ordered: sumOf(dcs, (d) => d.store_ordered),
    over_pct: percentOf(over, carryIn + factoryIn + transferIn - transferOut),
  };
};

/** The day as it will read once the draft is saved (แคปให้บัญชี needs no GM figure: it stays). */
export const previewDay = (day: BdcStockDay, draft: StockDayDraft): BdcStockDay => {
  const items = day.items.map((item) => previewItem(item, draft.items[item.barcode]));
  const dcs = day.dcs.map((dc) => previewDc(dc, draft.dcs[dc.dc_code]));
  return { ...day, items, dcs, totals: totalsOf(items, dcs) };
};

// ---- validation ----

export const QTY_ERROR = `จำนวนเต็ม 0–${STOCK_QTY_MAX.toLocaleString("en-US")}`;
export const NOTE_REQUIRED_ERROR = "ต้องใส่หมายเหตุ";
export const NOTE_LENGTH_ERROR = `ไม่เกิน ${STOCK_NOTE_MAX_LENGTH} ตัวอักษร`;

/** errors[barcode][field] / dcErrors[dc_code][field] — a Thai message per bad cell. */
export interface DraftErrors {
  items: Record<string, Partial<Record<ItemField, string>>>;
  dcs: Record<string, Partial<Record<DcField, string>>>;
  count: number;
}

/**
 * Bad numbers in the draft, and รับโอน / โอนออก without a note — checked on the merged row so a
 * stored note counts, as on the server.
 */
export const draftErrors = (day: BdcStockDay, draft: StockDayDraft): DraftErrors => {
  const result: DraftErrors = { items: {}, dcs: {}, count: 0 };
  const add = <K extends string>(bucket: Record<string, Partial<Record<K, string>>>, key: string, field: K, message: string) => {
    bucket[key] = { ...bucket[key], [field]: message };
    result.count += 1;
  };
  for (const item of day.items) {
    const own = draft.items[item.barcode];
    if (own) {
      for (const field of [...ZERO_FIELDS, ...NULLABLE_FIELDS]) {
        const text = own[field];
        if (text !== undefined && !parseQty(text).ok) add(result.items, item.barcode, field, QTY_ERROR);
      }
      for (const field of NOTE_FIELDS) {
        if ((own[field] ?? "").trim().length > STOCK_NOTE_MAX_LENGTH) {
          add(result.items, item.barcode, field, NOTE_LENGTH_ERROR);
        }
      }
    }
    const merged = previewItem(item, own);
    if (merged.transfer_in > 0 && merged.transfer_in_note === null) {
      add(result.items, item.barcode, "transfer_in_note", NOTE_REQUIRED_ERROR);
    }
    if (merged.transfer_out > 0 && merged.transfer_out_note === null) {
      add(result.items, item.barcode, "transfer_out_note", NOTE_REQUIRED_ERROR);
    }
  }
  for (const dc of day.dcs) {
    const own = draft.dcs[dc.dc_code];
    for (const field of ["store_total", "store_ordered"] as const) {
      const text = own?.[field];
      if (text !== undefined && !parseQty(text).ok) add(result.dcs, dc.dc_code, field, QTY_ERROR);
    }
  }
  return result;
};

// ---- the PUT body ----

const itemUpdateOf = (item: BdcStockDayItem, draft: ItemDraft): BdcStockDayItemUpdate | null => {
  const update: Record<string, number | string | null> = {};
  for (const field of Object.keys(draft) as ItemField[]) {
    const text = draft[field] as string;
    const stored = storedItemValue(item, field);
    if (isNoteField(field)) {
      const next = noteValue(text);
      if (next !== stored) update[field] = next;
      continue;
    }
    const parsed = parseQty(text);
    if (!parsed.ok) continue;
    const next = parsed.value === null && isZeroField(field) ? 0 : parsed.value;
    if (next !== stored) update[field] = next;
  }
  return Object.keys(update).length > 0 ? ({ barcode: item.barcode, ...update } as BdcStockDayItemUpdate) : null;
};

const dcUpdateOf = (dc: BdcStockDayDc, draft: DcDraft): BdcStockDayDcUpdate | null => {
  const update: BdcStockDayDcUpdate = { dc_code: dc.dc_code };
  let changed = false;
  for (const field of Object.keys(draft) as DcField[]) {
    const parsed = parseQty(draft[field] as string);
    if (!parsed.ok) continue;
    if (parsed.value !== storedDcValue(dc, field)) {
      update[field] = parsed.value;
      changed = true;
    }
  }
  return changed ? update : null;
};

/**
 * Only what changed: each changed line carries just its changed keys (an omitted key keeps
 * its value on the server; null clears it back to the system value). null when nothing changed.
 */
export const buildSaveBody = (
  day: BdcStockDay,
  draft: StockDayDraft,
  companyId?: string,
): BdcStockDaySaveRequest | null => {
  const items = day.items
    .map((item) => (draft.items[item.barcode] ? itemUpdateOf(item, draft.items[item.barcode]) : null))
    .filter((row): row is BdcStockDayItemUpdate => row !== null);
  const dcs = day.dcs
    .map((dc) => (draft.dcs[dc.dc_code] ? dcUpdateOf(dc, draft.dcs[dc.dc_code]) : null))
    .filter((row): row is BdcStockDayDcUpdate => row !== null);
  if (items.length === 0 && dcs.length === 0) return null;
  return {
    ...(companyId ? { company_id: companyId } : {}),
    ...(items.length > 0 ? { items } : {}),
    ...(dcs.length > 0 ? { dcs } : {}),
  };
};

// ---- draft edits ----

export const withItemText = (
  draft: StockDayDraft,
  barcode: string,
  field: ItemField,
  text: string,
): StockDayDraft => ({
  ...draft,
  items: { ...draft.items, [barcode]: { ...draft.items[barcode], [field]: text } },
});

export const withDcText = (draft: StockDayDraft, dcCode: string, field: DcField, text: string): StockDayDraft => ({
  ...draft,
  dcs: { ...draft.dcs, [dcCode]: { ...draft.dcs[dcCode], [field]: text } },
});

// ---- dates ----

const MS_PER_DAY = 86_400_000;

/** Inclusive day count of a `YYYY-MM-DD` range (negative/zero when reversed). */
export const rangeDays = (from: string, to: string): number =>
  Math.round((Date.parse(to) - Date.parse(from)) / MS_PER_DAY) + 1;

/** null when the range is fine, else why not (in Thai). */
export const reportRangeError = (from: string, to: string): string | null => {
  const days = rangeDays(from, to);
  if (!Number.isFinite(days) || days < 1) return "วันเริ่มต้องไม่เกินวันสิ้นสุด";
  if (days > STOCK_REPORT_MAX_DAYS) return `เลือกได้ไม่เกิน ${STOCK_REPORT_MAX_DAYS} วัน`;
  return null;
};

/** `%` with one decimal, or "-" when there is no base. */
export const pctText = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)}%`;
